import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { Page, QuoteForm } from "../components/site";
import { PriceCalculator } from "../components/price-calculator";
import { Kostentabel } from "../components/kostentabel";
import { dienstFoto } from "../lib/plaats-foto";
import {
  SITE,
  dienstBySlug,
  diensten,
  euro,
  lokaalBedrag,
  lokaalBereik,
  plaatsBySlug,
  plaatsIndex,
  plaatsen,
  voorrijkosten,
} from "../lib/seo-data";

export const Route = createFileRoute("/elektricien/$dienst/$plaats")({
  loader: ({ params }) => {
    const dienst = dienstBySlug(params.dienst);
    const plaats = plaatsBySlug(params.plaats);
    if (!dienst || !plaats) throw notFound();
    return { dienst, plaats };
  },
  head: ({ params, loaderData }) => {
    const d = loaderData?.dienst;
    const p = loaderData?.plaats;
    if (!d || !p) {
      return { meta: [{ title: "Pagina niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const lokaal = lokaalBereik(d, p.slug);
    const title = `${d.naam} in ${p.naam}: kosten ${lokaal} | offerto`;
    const description = `Wat kost ${d.naam.toLowerCase()} in ${p.naam}? Reken op ${lokaal}, gemiddeld ${euro(lokaalBedrag(d.gemiddeld, p.slug))}. Voorrijkosten ${voorrijkosten(p.slug)}, uurtarief ${d.uurtarief}. Vergelijk gratis offertes in ${p.provincie}.`;
    const url = `${SITE}/elektricien/${params.dienst}/${params.plaats}`;
    const foto = dienstFoto(d.slug, d.naam, p.naam);
    const fotoUrl = `${SITE}${foto.url}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "nl_NL" },
        { name: "geo.placename", content: p.naam },
        { name: "geo.region", content: "NL" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: fotoUrl },
        { property: "og:image:alt", content: foto.alt },
        { name: "twitter:image", content: fotoUrl },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `${d.naam} in ${p.naam}`,
            serviceType: d.naam,
            areaServed: {
              "@type": "City",
              name: p.naam,
              address: { "@type": "PostalAddress", addressRegion: p.provincie, addressCountry: "NL" },
            },
            provider: { "@type": "Organization", name: "offerto", url: SITE },
            image: {
              "@type": "ImageObject",
              url: fotoUrl,
              width: 1200,
              height: 630,
              caption: foto.alt,
            },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "EUR",
              lowPrice: lokaalBedrag(d.van, p.slug),
              highPrice: lokaalBedrag(d.tot, p.slug),
              offerCount: d.kosten.length,
              description: lokaal,
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${url}#localbusiness`,
            name: `offerto ${p.naam}`,
            description,
            url,
            areaServed: {
              "@type": "City",
              name: p.naam,
              address: { "@type": "PostalAddress", addressLocality: p.naam, addressRegion: p.provincie, addressCountry: "NL" },
            },
            address: { "@type": "PostalAddress", addressLocality: p.naam, addressRegion: p.provincie, addressCountry: "NL" },
            priceRange: "€€",
            currenciesAccepted: "EUR",
            image: fotoUrl,
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                opens: "08:00",
                closes: "18:00",
              },
            ],
            makesOffer: {
              "@type": "Offer",
              url,
              itemOffered: { "@type": "Service", name: `${d.naam} in ${p.naam}`, serviceType: d.naam },
              priceSpecification: {
                "@type": "PriceSpecification",
                priceCurrency: "EUR",
                minPrice: lokaalBedrag(d.van, p.slug),
                maxPrice: lokaalBedrag(d.tot, p.slug),
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: d.faq.map((f) => ({
              "@type": "Question",
              name: `${f.v} (${p.naam})`,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Diensten", item: `${SITE}/diensten` },
              { "@type": "ListItem", position: 2, name: d.naam, item: `${SITE}/elektricien/${params.dienst}` },
              { "@type": "ListItem", position: 3, name: p.naam, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: DienstPlaats,
});

function DienstPlaats() {
  const { dienst, plaats } = Route.useLoaderData();
  const foto = dienstFoto(dienst.slug, dienst.naam, plaats.naam);
  const andereDiensten = diensten.filter((d) => d.slug !== dienst.slug).slice(0, 4);
  const index = plaatsen.findIndex((p) => p.slug === plaats.slug);
  const buurplaatsen = [1, 2, 3, 4].map((n) => plaatsen[(index + n) % plaatsen.length]!);
  const factor = plaatsIndex(plaats.slug);
  const procent = Math.round(Math.abs(factor - 1) * 100);
  const niveauTekst =
    procent < 2
      ? "op"
      : factor > 1
        ? `ongeveer ${procent} procent boven`
        : `ongeveer ${procent} procent onder`;

  return (
    <Page
      title={`${dienst.h1} in ${plaats.naam}`}
      intro={`Vergelijk gratis en vrijblijvend tot 5 offertes van betrouwbare elektriciens in ${plaats.naam} en omgeving. Zo vind je de vakman uit jouw regio die bij je project past.`}
    >
      <nav className="mt-6 text-sm text-muted-foreground">
        <Link to="/diensten" className="hover:text-foreground">
          Alle elektricienklussen
        </Link>
        <span className="px-2">/</span>
        <Link
          to="/elektricien/$dienst"
          params={{ dienst: dienst.slug }}
          className="hover:text-foreground"
        >
          {dienst.naam}
        </Link>
        <span className="px-2">/</span>
        <span className="text-foreground">{plaats.naam}</span>
      </nav>

      <figure className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
        <img
          src={foto.url}
          alt={foto.alt}
          width={1200}
          height={630}
          fetchPriority="high"
          loading="eager"
          className="h-auto w-full object-cover"
        />
        <figcaption className="px-4 py-3 text-xs text-muted-foreground">
          {dienst.naam} in {plaats.naam} door een aangesloten erkende elektricien.
        </figcaption>
      </figure>


      <div className="mt-8 grid min-w-0 gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold">
            {dienst.naam} in {plaats.naam}: waar je op let
          </h2>
          <p className="mt-3 text-muted-foreground">
            {plaats.naam} ({plaats.provincie}, {plaats.inwoners} inwoners) kent {plaats.woningbeeld}.
            Dat bepaalt het werk: {dienst.intro.charAt(0).toLowerCase() + dienst.intro.slice(1)}
          </p>
          <p className="mt-3 text-muted-foreground">
            Elektriciens die via offerto werken, komen in heel {plaats.naam} - onder
            andere in {plaats.wijken.slice(0, -1).join(", ")} en {plaats.wijken.at(-1)} - en in de
            omliggende gemeenten in {plaats.provincie}. Bekijk ook{" "}
            <Link
              to="/plaatsen/$plaats"
              params={{ plaats: plaats.slug }}
              className="font-medium text-primary hover:underline"
            >
              alle tarieven van een elektricien in {plaats.naam}
            </Link>{" "}
            of{" "}
            <Link
              to="/elektricien/$dienst"
              params={{ dienst: dienst.slug }}
              className="font-medium text-primary hover:underline"
            >
              de landelijke kosten van {dienst.naam.toLowerCase()}
            </Link>
            .
          </p>

          <Kostentabel dienst={dienst} plaatsNaam={plaats.naam} plaatsSlug={plaats.slug} />

          <p className="mt-3 text-muted-foreground">
            Het prijsniveau in {plaats.naam} ligt {niveauTekst} het landelijk gemiddelde. Reken op{" "}
            <strong className="text-foreground">{voorrijkosten(plaats.slug)}</strong> aan
            voorrijkosten. In de oudere delen van {plaats.naam} valt de prijs vaak iets hoger uit
            omdat het leidingwerk lastiger bereikbaar is; in nieuwere wijken zoals{" "}
            {plaats.wijken.at(-1)} gaat het werk meestal sneller.
          </p>

          <PriceCalculator
            dienstSlug={dienst.slug}
            dienstNaam={dienst.naam}
            plaatsNaam={plaats.naam}
            plaatsSlug={plaats.slug}
          />

          <h2 className="mt-10 text-2xl font-bold">Het werk omvat</h2>
          <ul className="mt-4 space-y-2 text-muted-foreground">
            {dienst.werk.map((w) => (
              <li key={w} className="flex gap-2">
                <span className="text-primary">✓</span>
                {w}
              </li>
            ))}
          </ul>

          <h2 className="mt-10 text-2xl font-bold">Vragen van inwoners uit {plaats.naam}</h2>
          <dl className="mt-4 space-y-5">
            {dienst.faq.map((f) => (
              <div key={f.v}>
                <dt className="font-semibold">{f.v}</dt>
                <dd className="mt-1 text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>

        <aside>
          <QuoteForm compact />
          <div className="mt-8 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold">Andere klussen in {plaats.naam}</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {andereDiensten.map((d) => (
                <li key={d.slug}>
                  <Link
                    to="/elektricien/$dienst/$plaats"
                    params={{ dienst: d.slug, plaats: plaats.slug }}
                    className="hover:text-foreground"
                  >
                    {d.naam} in {plaats.naam}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/plaatsen/$plaats"
                  params={{ plaats: plaats.slug }}
                  className="font-medium text-primary hover:underline"
                >
                  Alle elektriciensklussen in {plaats.naam}
                </Link>
              </li>
            </ul>
          </div>
          <div className="mt-6 rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold">{dienst.naam} in de regio</h2>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {buurplaatsen.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/elektricien/$dienst/$plaats"
                    params={{ dienst: dienst.slug, plaats: p.slug }}
                    className="hover:text-foreground"
                  >
                    {dienst.naam} in {p.naam}: kosten {lokaalBereik(dienst, p.slug)}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/elektricien/$dienst"
                  params={{ dienst: dienst.slug }}
                  className="font-medium text-primary hover:underline"
                >
                  Landelijke kosten van {dienst.naam.toLowerCase()}
                </Link>
              </li>
              <li>
                <Link to="/plaatsen" className="font-medium text-primary hover:underline">
                  Elektricien in een andere plaats
                </Link>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </Page>
  );
}
